import { BoxItem } from 'box-ui-elements/es';
import { Fragment, ReactNode } from 'react';
import { Link, Typography } from '@common-components';
import { DeclinationType, FileType, SubmissionMarketRequestStatus } from 'enums';
import { SubmissionMarket, UpdateSubmissionMarketRequest } from 'types';
import { MarketAttributes } from 'broker/pages/SubmissionWorkspacePage/components/Header/RenewalDemo/markets/utils';
import { TooltipVideoPlayer } from 'broker/pages/SubmissionWorkspacePage/components/Header/RenewalDemo/steps/components/TooltipVideoPlayer';
import { EmailListItemAttributes } from 'broker/pages/SubmissionWorkspacePage/components/Toolbox/components/Email/utils';
import { ToolboxItemLink } from './components/ToolboxItemLink';
import { renderMarketingEmails } from './steps-utils';

export interface DemoStep {
  description: () => ReactNode;
  action: () => Promise<void>;
}

export const FetchingAccountInformationStep: DemoStep = {
  description: () => 'Fetching account information from data system',
  action: async () => {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 800);
    });
  },
};

export const createDataImportedFromAMSStep = (params: {
  expiringPolicy?: BoxItem;
  application?: BoxItem;
  onClose: () => void;
  fetchFromAMS: (submissionId: string) => Promise<void | null>;
  reFetchFileItems: () => Promise<void>;
  reFetchWorkspace: () => Promise<void>;
  submissionId: string;
}): DemoStep => ({
  description: () => (
    <Typography variant="body2">
      {`Data imported from data system: `}
      <ToolboxItemLink entityId={params.expiringPolicy?.id} itemType="file" onClick={params.onClose}>
        Expiring policy
      </ToolboxItemLink>
      {', '}
      <ToolboxItemLink entityId={params.application?.id} itemType="file" onClick={params.onClose}>
        Renewal Application
      </ToolboxItemLink>
    </Typography>
  ),
  action: async () => {
    await params.fetchFromAMS(params.submissionId);
    await params.reFetchFileItems();
    await params.reFetchWorkspace();
  },
});

export const createMarketingStrategyAnalysisStep = (params: { markets: MarketAttributes[] }): DemoStep => ({
  description: () =>
    `Marketing strategy analysis: ${params.markets
      .map((market) => `${market.name}${market.isIncumbent ? ' (incumbent)' : ''}`)
      .join(', ')}`,
  action: async () => {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 1500);
    });
  },
});

export const createRemarketingDecisionStep = (params: {
  expiringPolicy?: BoxItem;
  renewalPolicy?: BoxItem;
  onClose: () => void;
}): DemoStep => ({
  description: () => (
    <Typography variant="body2">
      {`Evaluating re-marketing criteria: Comparing `}
      <ToolboxItemLink entityId={params.renewalPolicy?.id} itemType="file" onClick={params.onClose}>
        Renewal Policy
      </ToolboxItemLink>
      {` with the `}
      <ToolboxItemLink entityId={params.expiringPolicy?.id} itemType="file" onClick={params.onClose}>
        Expiring Policy
      </ToolboxItemLink>
      . Found 35% premium increase (threshold is 10%). Proceeding to re-market to additional markets.
    </Typography>
  ),

  action: async () => {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 1500);
    });
  },
});

export const createApiMarketingStep = (params: {
  markets: MarketAttributes[];
  marketViaAPI: (submissionId: string) => Promise<void | null>;
  reFetchWorkspace: () => Promise<void>;
  submissionId: string;
}): DemoStep => ({
  description: () =>
    `Placing with ${params.markets
      .filter((market) => market.method === 'API')
      .map((market) => market.name)
      .join(', ')} over API`,
  action: async () => {
    await params.marketViaAPI(params.submissionId);
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 300);
    });
    await params.reFetchWorkspace();
  },
});

export const createEmailMarketingStep = (params: {
  markets: MarketAttributes[];
  marketViaEmail: (submissionId: string) => Promise<void | null>;
  reFetchSubmissionEmails: () => Promise<void>;
  reFetchWorkspace: () => Promise<void>;
  onClose: () => void;
  marketingEmails: EmailListItemAttributes[];
  submissionId: string;
}): DemoStep => ({
  description: () => (
    <Typography variant="body2">
      {`Marketing via email to `}
      <>{renderMarketingEmails(params.markets, params.onClose, params.marketingEmails)}</>
    </Typography>
  ),
  action: async () => {
    await params.marketViaEmail(params.submissionId);
    await params.reFetchSubmissionEmails();
    await params.reFetchWorkspace();
  },
});

export const createPortalMarketingStep = (params: {
  markets: MarketAttributes[];
  marketViaPortal: (submissionId: string) => Promise<void | null>;
  reFetchWorkspace: () => Promise<void>;
  submissionId: string;
  videoUrl?: string;
}): DemoStep => ({
  description: () => {
    const portalMarkets = params.markets.filter((market) => market.method === 'portal' && !market.isIncumbent);

    return (
      <Typography variant="body2">
        {'Marketing via AI Portal Agent to '}
        <TooltipVideoPlayer videoUrl={params.videoUrl}>
          <Typography variant="body2" component="span">
            {portalMarkets.map((market, index) => (
              <Fragment key={market.name}>
                <Link underline="hover" sx={{ cursor: 'pointer' }} href={market.portalUrl} target="_blank">
                  {market.name}
                </Link>
                {index < portalMarkets.length - 1 && ', '}
              </Fragment>
            ))}
          </Typography>
        </TooltipVideoPlayer>
      </Typography>
    );
  },
  action: async () => {
    await params.marketViaPortal(params.submissionId);
    await params.reFetchWorkspace();
  },
});

const getMarketRequestByName = (markets: SubmissionMarket[], marketName: string) =>
  markets.find((market) => market.marketName.includes(marketName))?.marketRequests[0];

export const createDeclinationStep = (params: {
  declinedMarketNames: string[];
  markets: SubmissionMarket[];
  updateSubmissionMarketRequest: (
    id: string,
    data: UpdateSubmissionMarketRequest,
    skipRefetch?: boolean,
  ) => Promise<void>;
}): DemoStep => ({
  description: () => `Received a declination from ${params.declinedMarketNames.join(', ')}`,
  action: async () => {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 10000);
    });
    await Promise.all(
      params.declinedMarketNames.map(async (marketName) => {
        const marketRequest = getMarketRequestByName(params.markets, marketName);

        if (!marketRequest) {
          return;
        }

        await params.updateSubmissionMarketRequest(
          marketRequest.id,
          {
            status: SubmissionMarketRequestStatus.Declined,
            declination: {
              declinationReasons: [
                {
                  type: DeclinationType.OTHER,
                  details: 'Industry is an automatic decline based on underwriting guidelines',
                },
              ],
              isAIExtracted: false,
            },
          },
          true,
        );
      }),
    );
  },
});

export const createDeferredToUnderwriterStep = (params: {
  deferredMarketNames: string[];
  markets: SubmissionMarket[];
  updateSubmissionMarketRequest: (
    id: string,
    data: UpdateSubmissionMarketRequest,
    skipRefetch?: boolean,
  ) => Promise<void>;
}): DemoStep => ({
  description: () => `Received a notification from ${params.deferredMarketNames.join(', ')} account deferred to an UW`,
  action: async () => {
    await Promise.all(
      params.deferredMarketNames.map(async (marketName) => {
        const marketRequest = getMarketRequestByName(params.markets, marketName);

        if (!marketRequest) {
          return;
        }

        await params.updateSubmissionMarketRequest(
          marketRequest.id,
          { status: SubmissionMarketRequestStatus.AwaitingQuote, notes: 'Account has been deferred to an underwriter' },
          true,
        );
      }),
    );
  },
});

export const createQuotesReceivedStep = (params: {
  quotingMarketNames: string[];
  marketQuotes: Array<BoxItem>;
  receiveQuotes: (params: {
    submissionId: string;
    quoteType?: FileType.MarketQuote | FileType.RenewalPolicy;
  }) => Promise<void | null>;
  reFetchFileItems: () => Promise<void>;
  reFetchWorkspace: () => Promise<void>;
  onClose: () => void;
  submissionId: string;
}): DemoStep => ({
  description: () => {
    const getQuote = (marketName: string) =>
      params.marketQuotes.find((file) => file.name.toLowerCase().includes(marketName.toLowerCase()));

    return (
      <Typography variant="body2">
        {`Received ${params.quotingMarketNames.length === 1 ? 'a quote' : 'quotes'} from `}
        {params.quotingMarketNames.map((market, index) => (
          <Fragment key={market}>
            <ToolboxItemLink entityId={getQuote(market)?.id} itemType="file" onClick={params.onClose}>
              {market}
            </ToolboxItemLink>
            {index < params.quotingMarketNames.length - 1 && ' and '}
          </Fragment>
        ))}
      </Typography>
    );
  },
  action: async () => {
    await params.receiveQuotes({ submissionId: params.submissionId, quoteType: FileType.MarketQuote });
    await params.reFetchFileItems();
    await params.reFetchWorkspace();
  },
});

export const createAutomatedRenewalPolicyStep = (params: {
  renewalPolicy?: BoxItem;
  receiveQuotes: ({
    submissionId,
    quoteType,
  }: {
    submissionId: string;
    quoteType?: FileType.MarketQuote | FileType.RenewalPolicy;
  }) => Promise<void | null>;
  reFetchFileItems: () => Promise<void>;
  reFetchWorkspace: () => Promise<void>;
  submissionId: string;
  onClose: () => void;
}): DemoStep => ({
  description: () => (
    <Typography variant="body2">
      {'Importing Automated '}
      <ToolboxItemLink entityId={params.renewalPolicy?.id} itemType="file" onClick={params.onClose}>
        Renewal Policy
      </ToolboxItemLink>
    </Typography>
  ),
  action: async () => {
    await params.receiveQuotes({ submissionId: params.submissionId, quoteType: FileType.RenewalPolicy });
    await params.reFetchFileItems();
    await params.reFetchWorkspace();
  },
});

export const createAnalyzeQuotesStep = (params: {
  markets: MarketAttributes[];
  marketQuotes: Array<BoxItem>;
  onClose: () => void;
}): DemoStep => ({
  description: () => {
    const getQuote = (marketName: string) =>
      params.marketQuotes.find((file) => file.name.toLowerCase().includes(marketName.toLowerCase()));

    const candidateMarkets = params.markets.filter(
      (market) => market.status === 'quoted' || market.status === 'renewal',
    );
    return (
      <Typography variant="body2">
        {'Analyzed quotes '}
        {candidateMarkets.map((market, index) => (
          <Fragment key={market.name}>
            <ToolboxItemLink entityId={getQuote(market.name)?.id} itemType="file" onClick={params.onClose}>
              {market.status === 'renewal' ? 'Renewal Policy' : market.name}
            </ToolboxItemLink>
            {index < candidateMarkets.length - 1 && ', '}
          </Fragment>
        ))}
      </Typography>
    );
  },
  action: async () => {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 1300);
    });
  },
});

export const createProposalCreationStep = (params: {
  createProposal: (submissionId: string) => Promise<void | null>;
  proposalFile?: BoxItem;
  reFetchFileItems: () => Promise<void>;
  reFetchWorkspace: () => Promise<void>;
  submissionId: string;
  onClose: () => void;
}): DemoStep => ({
  description: () => (
    <Typography variant="body2">
      {'Creating a '}
      <ToolboxItemLink entityId={params.proposalFile?.id} itemType="file" onClick={params.onClose}>
        Proposal
      </ToolboxItemLink>
    </Typography>
  ),
  action: async () => {
    await params.createProposal(params.submissionId);
    await params.reFetchFileItems();
    await params.reFetchWorkspace();
  },
});

export const createReviewProposal = (params: { navigateToProposal: () => void }): DemoStep => ({
  description: () => (
    <Typography variant="body2">
      {'Notifying Placement Specialist to '}
      <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={params.navigateToProposal}>
        review Proposal
      </Link>
    </Typography>
  ),
  action: async () => {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 700);
    });
  },
});

export const WritingInformationBackToAMS: DemoStep = {
  description: () => 'Writing information back to data system',
  action: async () => {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 1000);
    });
  },
};
export const createInformationSavedToAMSStep = (params: {
  writeToAMS: (submissionId: string) => Promise<void | null>;
  submissionId: string;
}): DemoStep => ({
  description: () => 'data system has been updated',
  action: async () => {
    await params.writeToAMS(params.submissionId);
  },
});
